import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, Users, Camera, Heart, Hash } from 'lucide-react';
import { toast } from 'sonner';

interface SearchResult {
  type: 'user' | 'post' | 'pet' | 'hashtag';
  id: string;
  title: string;
  subtitle?: string;
  avatar?: string;
  username?: string;
  link: string;
}

interface GlobalSearchProps {
  placeholder?: string;
  className?: string;
  onResultClick?: () => void;
}

export const GlobalSearch = ({ 
  placeholder = "Cerca pet, persone, luoghi...", 
  className = "",
  onResultClick 
}: GlobalSearchProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (query.trim()) {
      const debounceTimer = setTimeout(() => {
        performSearch(query);
      }, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setResults([]);
      setIsOpen(false);
    }
  }, [query]);

  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim() || !user) return;

    setLoading(true);
    try {
      const searchResults: SearchResult[] = [];

      // Search users
      const { data: usersData } = await supabase
        .from('profiles')
        .select('id, user_id, username, display_name, avatar_url')
        .or(`username.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%`)
        .limit(5);

      usersData?.forEach(user => {
        searchResults.push({
          type: 'user',
          id: user.id,
          title: user.display_name || user.username,
          subtitle: `@${user.username}`,
          avatar: user.avatar_url,
          username: user.username,
          link: `/app/user/${user.username}`
        });
      });

      // Search pets
      const { data: petsData } = await supabase
        .from('pets')
        .select('id, name, species, breed, avatar_url, user_id')
        .eq('is_public', true)
        .or(`name.ilike.%${searchQuery}%,breed.ilike.%${searchQuery}%,species.ilike.%${searchQuery}%`)
        .limit(5);

      if (petsData && petsData.length > 0) {
        // Ottimizzato: una sola query invece di N query
        const userIds = [...new Set(petsData.map(pet => pet.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, username')
          .in('user_id', userIds);

        const profilesMap = Object.fromEntries(
          (profilesData || []).map(p => [p.user_id, p])
        );

        const petsWithProfiles = petsData.map(pet => ({
          ...pet,
          profile: profilesMap[pet.user_id] || null
        }));

        petsWithProfiles.forEach(pet => {
          if (pet.profile) {
            searchResults.push({
              type: 'pet',
              id: pet.id,
              title: pet.name,
              subtitle: `${pet.species}${pet.breed ? ` - ${pet.breed}` : ''} di @${pet.profile.username}`,
              avatar: pet.avatar_url,
              username: pet.profile.username,
              link: `/app/user/${pet.profile.username}`
            });
          }
        });
      }

      // Search posts
      const { data: postsData } = await supabase
        .from('posts')
        .select('id, content, image_url, user_id')
        .eq('is_public', true)
        .or(`content.ilike.%${searchQuery}%,location.ilike.%${searchQuery}%`)
        .limit(3);

      if (postsData) {
        const postsWithProfiles = await Promise.all(
          postsData.map(async (post) => {
            const { data: profileData } = await supabase
              .from('profiles')
              .select('username, display_name, avatar_url')
              .eq('user_id', post.user_id)
              .single();

            return {
              ...post,
              profile: profileData
            };
          })
        );

        postsWithProfiles.forEach(post => {
          if (post.profile) {
            const contentPreview = post.content?.substring(0, 50) + (post.content && post.content.length > 50 ? '...' : '');
            searchResults.push({
              type: 'post',
              id: post.id,
              title: contentPreview || 'Post con immagine',
              subtitle: `di @${post.profile.username}`,
              avatar: post.profile.avatar_url,
              username: post.profile.username,
              link: `/app/post/${post.id}`
            });
          }
        });
      }

      // Add hashtag if query starts with #
      if (searchQuery.startsWith('#')) {
        searchResults.push({
          type: 'hashtag',
          id: searchQuery,
          title: searchQuery,
          subtitle: 'Cerca questo hashtag',
          link: `/app/explore?q=${encodeURIComponent(searchQuery)}`
        });
      }

      setResults(searchResults);
      setIsOpen(searchResults.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Errore durante la ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.link);
    setQuery('');
    setIsOpen(false);
    onResultClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    } else if (e.key === 'Enter' && results.length > 0) {
      handleResultClick(results[0]);
    }
  };

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'user': return <Users className="w-4 h-4" />;
      case 'pet': return <Heart className="w-4 h-4" />;
      case 'post': return <Camera className="w-4 h-4" />;
      case 'hashtag': return <Hash className="w-4 h-4" />;
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query && setIsOpen(true)}
          className="pl-10 glass border-white/20 focus:border-primary"
        />
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <Card className="absolute top-full left-0 right-0 mt-2 z-50 glass border-white/20 shadow-2xl backdrop-blur-xl">
          <CardContent className="p-2 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                <p className="text-sm text-muted-foreground mt-2">Ricerca in corso...</p>
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-1">
                {results.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                  >
                    <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
                      {result.avatar ? (
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={result.avatar} />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {result.title.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        getIcon(result.type)
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {result.title}
                      </p>
                      {result.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0 text-xs text-muted-foreground">
                      {result.type === 'user' ? 'Utente' : 
                       result.type === 'pet' ? 'Pet' : 
                       result.type === 'post' ? 'Post' : 'Hashtag'}
                    </div>
                  </button>
                ))}
                
                {query && (
                  <button
                    onClick={() => navigate(`/app/explore?q=${encodeURIComponent(query)}`)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors text-left border-t border-white/10 mt-2 pt-3"
                  >
                    <Search className="w-4 h-4 text-primary" />
                    <span className="text-sm text-muted-foreground">
                      Vedi tutti i risultati per "<span className="text-white">{query}</span>"
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <div className="p-4 text-center">
                <Search className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Nessun risultato trovato</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};